import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { ExternalLink, Loader2, X } from "lucide-react";
import { settlementsApi } from "../api";
import { Section, Field, Link } from "../../applications/shared/InfoBlock";
import { StatusBadge } from "../../applications/shared/StatusBadge";
import { formatApplicantName, formatDateTime } from "../../applications/shared/format";
import { fmtCLP } from "../../dashboard/format";
import {
  DISPUTE_STATUS_LABELS,
  DISPUTE_TYPE_LABELS,
  SETTLEMENT_STATUS_LABELS,
  disputeStatusTone,
  settlementStatusTone,
} from "./labels";

interface Props {
  settlementId: number | null;
  onClose: () => void;
  /** Slot opcional para acciones contextuales (footer) — distintas en cada sub-tab. */
  renderActions?: (data: NonNullable<ReturnType<typeof useFullQuery>["data"]>) => React.ReactNode;
}

function useFullQuery(id: number | null) {
  return useQuery({
    queryKey: ["settlements", "full", id],
    queryFn: () => settlementsApi.fullDetail(id!),
    enabled: id !== null,
  });
}

export function SettlementDrawer({ settlementId, onClose, renderActions }: Props) {
  const query = useFullQuery(settlementId);

  useEffect(() => {
    const h = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  if (settlementId === null) return null;

  const data = query.data;

  return (
    <>
      <div
        className="fixed inset-0 bg-black/40 z-40 animate-fade-in"
        onClick={onClose}
        aria-hidden
      />
      <aside
        className="fixed top-0 right-0 bottom-0 z-50 w-full sm:w-[640px] bg-bg-card border-l border-border shadow-xl flex flex-col animate-slide-in-right"
        role="dialog"
        aria-modal="true"
      >
        <header className="flex items-start justify-between gap-3 px-5 py-4 border-b border-border">
          <div className="min-w-0">
            <h2 className="nx-h2 truncate">
              Liquidación #{settlementId}
              {data?.settlement.event && (
                <span className="text-fg-muted font-normal text-base ml-2">
                  · {data.settlement.event.nombre}
                </span>
              )}
            </h2>
            {data?.settlement && (
              <p className="text-2xs text-fg-subtle mt-0.5">
                Creada {formatDateTime(data.settlement.createdAt)}
              </p>
            )}
          </div>
          <button onClick={onClose} className="nx-btn-ghost p-1.5" aria-label="Cerrar">
            <X size={16} />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {query.isLoading || !data ? (
            <div className="flex items-center justify-center py-12 text-fg-subtle">
              <Loader2 size={16} className="animate-spin mr-2" />
              Cargando detalle…
            </div>
          ) : (
            <div className="flex flex-col gap-5">
              <div className="flex items-center gap-2 flex-wrap">
                <StatusBadge
                  status={data.settlement.status}
                  tone={settlementStatusTone(data.settlement.status)}
                  label={SETTLEMENT_STATUS_LABELS[data.settlement.status]}
                />
                {data.settlement.acceptanceType && (
                  <span className="nx-badge-neutral">
                    Aceptación {data.settlement.acceptanceType === "tacit" ? "tácita" : "explícita"}
                  </span>
                )}
              </div>

              <Section title="Montos">
                <Field label="Bruto" value={fmtCLP(Number(data.settlement.grossRevenue))} />
                <Field label="Refunds" value={fmtCLP(Number(data.settlement.totalRefunded))} />
                <Field label="Comisión Mixcore" value={fmtCLP(Number(data.settlement.commissionAmount))} />
                <Field label="Comisión MP" value={fmtCLP(Number(data.settlement.mpFeeAmount))} />
                <Field
                  label="A transferir"
                  value={
                    <strong className="text-success">
                      {fmtCLP(Number(data.settlement.amountToTransfer))}
                    </strong>
                  }
                  wide
                />
              </Section>

              {data.settlement.event && (
                <Section title="Evento">
                  <Field label="Nombre" value={data.settlement.event.nombre} wide />
                  <Field label="Club" value={data.settlement.event.club?.name || data.settlement.event.club?.nombre || "—"} />
                  <Field label="Fecha" value={data.settlement.event.fecha ?? "—"} />
                  <Field
                    label="Productor"
                    value={formatApplicantName(data.settlement.event.usuario)}
                  />
                  <Field
                    label="Email productor"
                    value={
                      data.settlement.event.usuario?.email ? (
                        <a className="text-accent hover:underline" href={`mailto:${data.settlement.event.usuario.email}`}>
                          {data.settlement.event.usuario.email}
                        </a>
                      ) : (
                        "—"
                      )
                    }
                  />
                </Section>
              )}

              <Section title="Flow">
                <Field label="Inicio revisión" value={formatDateTime(data.settlement.revisionStartedAt)} />
                <Field label="Deadline revisión" value={formatDateTime(data.settlement.revisionDeadlineAt)} />
                <Field label="Aceptada" value={formatDateTime(data.settlement.acceptedAt)} />
                <Field label="Disputada" value={formatDateTime(data.settlement.disputedAt)} />
                <Field label="Resuelta" value={formatDateTime(data.settlement.resolvedAt)} />
                <Field label="Transferida" value={formatDateTime(data.settlement.transferredAt)} />
              </Section>

              {data.disputes.length > 0 && (
                <Section title={`Disputas (${data.disputes.length})`}>
                  <div className="col-span-2 flex flex-col gap-2">
                    {data.disputes.map((d) => (
                      <div key={d.id} className="border border-border-subtle rounded-md p-2.5 text-xs">
                        <div className="flex items-center justify-between mb-1.5 gap-2 flex-wrap">
                          <span className="font-semibold text-fg">
                            #{d.id} · {DISPUTE_TYPE_LABELS[d.tipoDisputa]}
                          </span>
                          <StatusBadge
                            status={d.statusDisputa}
                            tone={disputeStatusTone(d.statusDisputa)}
                            label={DISPUTE_STATUS_LABELS[d.statusDisputa]}
                          />
                        </div>
                        <div className="text-2xs text-fg-subtle mb-1">
                          {formatDateTime(d.createdAt)} ·{" "}
                          {d.createdByUser ? formatApplicantName(d.createdByUser) : "—"}
                        </div>
                        <div className="text-fg-muted whitespace-pre-wrap">{d.motivoDisputa}</div>
                        {d.expectedAmount !== null && (
                          <div className="text-2xs text-fg-subtle mt-1">
                            Esperado: <strong>{fmtCLP(d.expectedAmount)}</strong>
                          </div>
                        )}
                        {d.evidenciaUrl && (
                          <Link href={d.evidenciaUrl}>
                            <span className="inline-flex items-center gap-1 mt-1 text-2xs">
                              <ExternalLink size={10} /> Evidencia del productor
                            </span>
                          </Link>
                        )}
                        {d.minutaResolucion && (
                          <div className="mt-2 pt-2 border-t border-border-subtle">
                            <div className="text-2xs text-fg-subtle mb-1">
                              Resolución · {formatDateTime(d.resueltaAt)}
                            </div>
                            <div className="text-fg-muted whitespace-pre-wrap">{d.minutaResolucion}</div>
                            {d.montoAjuste !== null && d.montoAjuste !== 0 && (
                              <div className="text-2xs mt-1">
                                Ajuste:{" "}
                                <strong
                                  className={d.montoAjuste > 0 ? "text-success" : "text-danger"}
                                >
                                  {d.montoAjuste > 0 ? "+" : ""}
                                  {fmtCLP(d.montoAjuste)}
                                </strong>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </Section>
              )}

              {data.transferRecord && (
                <Section title="Transferencia">
                  <Field label="N° operación" value={data.transferRecord.numeroOperacion} />
                  <Field label="Banco" value={data.transferRecord.bancoEmisor} />
                  <Field label="Fecha" value={data.transferRecord.fechaTransferencia} />
                  <Field
                    label="Registrada por"
                    value={data.transferRecord.registradoPorUser?.email ?? "—"}
                  />
                  <Field
                    label="Comprobantes"
                    value={
                      data.transferRecord.comprobantesSignedUrls?.length ? (
                        <div className="flex flex-col gap-1">
                          {data.transferRecord.comprobantesSignedUrls.map((url, i) => (
                            <Link key={i} href={url}>
                              <span className="inline-flex items-center gap-1 text-2xs">
                                <ExternalLink size={10} /> Comprobante {i + 1}
                              </span>
                            </Link>
                          ))}
                        </div>
                      ) : (
                        "—"
                      )
                    }
                    wide
                  />
                  {data.transferRecord.notas && (
                    <Field label="Notas" value={<p className="whitespace-pre-wrap text-fg">{data.transferRecord.notas}</p>} wide />
                  )}
                </Section>
              )}

              {data.auditLog.length > 0 && (
                <Section title={`Bitácora (${data.auditLog.length})`}>
                  <div className="col-span-2 flex flex-col gap-1.5">
                    {data.auditLog.map((entry) => (
                      <div
                        key={entry.id}
                        className="border border-border-subtle rounded-md p-2 text-2xs"
                      >
                        <div className="flex items-center justify-between mb-0.5">
                          <span className="font-semibold text-fg">{entry.evento}</span>
                          <span className="text-fg-subtle">{formatDateTime(entry.timestamp)}</span>
                        </div>
                        <div className="text-fg-subtle">
                          {entry.fromStatus ?? "—"} → {entry.toStatus ?? "—"} ·{" "}
                          {entry.user ? formatApplicantName(entry.user) : "Sistema"}
                        </div>
                      </div>
                    ))}
                  </div>
                </Section>
              )}

              {data.settlement.notes && (
                <Section title="Notas">
                  <Field
                    label="Contenido"
                    value={<p className="whitespace-pre-wrap text-fg">{data.settlement.notes}</p>}
                    wide
                  />
                </Section>
              )}
            </div>
          )}
        </div>

        {data && renderActions && (
          <footer className="px-5 py-3 border-t border-border bg-bg-surface flex flex-wrap items-center gap-2 justify-end">
            {renderActions(data)}
          </footer>
        )}
      </aside>
    </>
  );
}
